import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons';
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormikErrors, useFormik } from 'formik';
import * as React from 'react';
import { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import VoxelAlert from '../../components/voxel/Alert/VoxelAlert';
import VoxelButton from '../../components/voxel/Buttons/VoxelButton';
import VoxelIconButton from '../../components/voxel/Buttons/VoxelIconButton';
import VoxelInput from '../../components/voxel/Forms/VoxelInput';
import Logo from '../../components/voxel/Logo/Logo';
import VoxelText from '../../components/voxel/Text/VoxelText';
import { useActiveTabContext } from '../../logic/activeTabContext/useActiveTabContext';
import { getApiOrigin } from '../../logic/api/getApiOirigin';
import { AuthenticationContext } from '../../logic/security/authentication/AuthenticationContext';
import moment from 'moment';

export interface RegisterTeamProps {
  token?: string;
  promoCode?: string;
}

interface FormState {
  teamName: string;
  viewExistingTeams: boolean;
  promoCode: string;
}

const RegisterTeam: React.FC<RegisterTeamProps> = ({ token, promoCode }) => {
  const authenticationContext = useContext(AuthenticationContext);

  const history = useHistory();
  const activeTabContext = useActiveTabContext();

  // Existing teams data retrieval
  // ... This is very vanilla without GraphQL...
  const [existingTeams, setExistingTeams] = useState<{ id: number; name: string }[]>(undefined);
  useEffect(() => {
    if (authenticationContext.authenticated) {
      const apiOrigin = getApiOrigin();
      authenticationContext.getIdToken().then((idToken) =>
        fetch(apiOrigin + '/api/signup/teams', {
          method: 'GET',
          headers: {
            authorization: `Bearer ${idToken}`,
            'Content-Type': 'application/json;charset=UTF-8',
            Accept: 'application/json; charset=utf-8',
          },
        })
          .then((result) => result.json())
          .then((result) => setExistingTeams(result?.teams))
          .catch(() => setExistingTeams([]))
      );
    }
  }, [authenticationContext.authenticated]);

  // Join existing team function
  const handleJoinExistingTeam = (teamId: number) => {
    setLoading(true);
    setSubmitError(null);
    const apiOrigin = getApiOrigin();
    return authenticationContext
      .getIdToken()
      .then((idToken) =>
        fetch(apiOrigin + '/api/signup/team-join', {
          method: 'POST',
          headers: {
            authorization: `Bearer ${idToken}`,
            'Content-Type': 'application/json;charset=UTF-8',
            Accept: 'application/json; charset=utf-8',
          },
          body: JSON.stringify({ teamId }),
        })
      )
      .then(() => {
        activeTabContext.setCurrentTeamId(teamId, true);
        history.push('/');
        window.location.reload();
      })
      .catch((error) => {
        console.error(error);
        setSubmitError(error);
      })
      .finally(() => setLoading(false));
  };

  // Register form
  const [loading, setLoading] = useState(false);
  const [promoError, setPromoError] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const formikProps = useFormik<FormState>({
    enableReinitialize: true,
    initialValues: {
      teamName: '',
      promoCode: promoCode ?? '',
      viewExistingTeams: !!existingTeams?.length,
    },
    validate: (values) => {
      const errors: FormikErrors<FormState> = {};
      if (values.teamName === '') {
        errors.teamName = 'Required';
      }
      return errors;
    },
    onSubmit: (values, actions) => {
      setLoading(true);
      setSubmitError(null);
      const apiOrigin = getApiOrigin();
      return authenticationContext.getIdToken().then((idToken) => {
        let promoValidationPromise = Promise.resolve();

        // Validate promo if provided
        if (!!values.promoCode?.trim()?.length) {
          promoValidationPromise = fetch(apiOrigin + '/api/signup/validatepromo', {
            method: 'POST',
            headers: {
              authorization: `Bearer ${idToken}`,
              'Content-Type': 'application/json;charset=UTF-8',
              Accept: 'application/json; charset=utf-8',
            },
            body: JSON.stringify({ promoCode: values.promoCode }),
          })
            .then((promoResult) => promoResult?.json())
            .catch(() => {
              throw new Error('Promo code not found');
            })
            .then((promoJsonResult) => {
              if (moment.utc(promoJsonResult?.expirationDate).isBefore()) {
                setPromoError(true);
                throw new Error('This promo code has expired');
              }
            });
        }

        // Register team
        return promoValidationPromise
          .then(() =>
            fetch(apiOrigin + '/api/signup/register', {
              method: 'POST',
              headers: {
                authorization: `Bearer ${idToken}`,
                'Content-Type': 'application/json;charset=UTF-8',
                Accept: 'application/json; charset=utf-8',
              },
              body: JSON.stringify({
                teamName: values.teamName,
                token,
                promoCode: values.promoCode,
              }),
            })
          )
          .then((result) => result.json())
          .then((jsonResult) => {
            const teamId = jsonResult.teamId;
            activeTabContext.setCurrentTeamId(teamId, true);
            history.push('/register/members');
            window.location.reload();
          })
          .catch((error) => {
            console.error(error?.message);
            setSubmitError(error?.message);
          })
          .finally(() => setLoading(false));
      });
    },
  });

  if (existingTeams === undefined) {
    return (
      <div className={`w-full h-screen bg-white flex flex-row relative`}>
        <div className="w-auto h-auto absolute top-8 left-8">
          <Logo size="xs" />
        </div>
      </div>
    );
  }

  if (formikProps.values.viewExistingTeams) {
    return (
      <div className={`w-full h-screen bg-white flex flex-row relative`}>
        <div className="w-auto h-auto absolute top-8 left-8">
          <Logo size="xs" />
        </div>
        <div className="mt-64 mx-auto w-full flex flex-col items-center gap-4">
          <VoxelText>Select your team</VoxelText>
          <div className="w-64 flex flex-col items-center gap-4">
            {existingTeams.map((t) => (
              <VoxelButton
                key={t.id}
                title={t.name}
                fullWidth
                size="xl"
                color="primary"
                disabled={loading}
                onClick={() => handleJoinExistingTeam(t.id)}
              />
            ))}
            <VoxelButton
              title={'Create a new team'}
              fullWidth
              size="xl"
              disabled={loading}
              onClick={() =>
                formikProps.setValues((prev) => ({ ...prev, viewExistingTeams: false }))
              }
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full h-screen bg-white flex flex-row relative`}>
      <div className="w-auto h-auto absolute top-8 left-8">
        <Logo size="xs" />
      </div>
      <form
        className="w-[600px] mt-64 mx-auto flex flex-col gap-10"
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          formikProps.submitForm();
        }}
      >
        <div className="flex flex-col gap-6">
          <div className="">
            <VoxelText
              userSelectClass="select-none"
              className="whitespace-pre-line font-semibold"
              variant="title2"
              paddingBot
            >
              Create your Voxel account
            </VoxelText>
            <VoxelText variant="bread-medium" color="light">
              Voxel is a keyword research tool for professionals where you may choose to invite
              other users to collaborate or share your work with clients.
            </VoxelText>
          </div>
        </div>
        <div className="flex flex-row items-end gap-4 w-[600px]">
          <div className="grow">
            <VoxelInput
              autoFocus
              size="xxl"
              inputWidthClass="w-full"
              placeholder="My team"
              labelPosition="top"
              label="Team account name"
              value={formikProps.values.teamName}
              onChange={(event) => formikProps.setFieldValue('teamName', event.target.value)}
              outlined
            />
          </div>
          <div className="w-40">
            <VoxelInput
              labelPosition="top"
              size="xxl"
              inputWidthClass="w-full"
              label="Promo code (optional)"
              value={formikProps.values.promoCode}
              onChange={(event) => formikProps.setFieldValue('promoCode', event.target.value)}
              outlined
            />
          </div>
          <div className="grow-0">
            <VoxelIconButton
              onClick={formikProps.submitForm}
              size={'lg'}
              color="primary"
              icon={<FontAwesomeIcon icon={faArrowRight} size="xl" />}
              disabled={
                loading || formikProps.isSubmitting || !formikProps.dirty || !formikProps.isValid
              }
            />
          </div>
        </div>

        {!!existingTeams?.length && (
          <div className="w-[600px] opacity-50">
            <VoxelButton
              size="sm"
              onClick={() =>
                formikProps.setValues((prev) => ({ ...prev, viewExistingTeams: true }))
              }
              startIcon={<FontAwesomeIcon icon={faChevronLeft} />}
              title="Back"
              color="light-gray"
              type="submit"
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                formikProps.submitForm();
              }}
            />
          </div>
        )}
        {!!submitError?.length && <VoxelAlert severity="error" title={submitError} />}
      </form>
    </div>
  );
};

export default RegisterTeam;
